import { useSession } from '@lendi/lala-react';
import Cookies from 'js-cookie';
import { useFlags, useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { ReactNode, useEffect, createContext, useState } from 'react';
import { TrackingService } from '../utils/helpers/tracking';
import { EventCategory, CustomerType } from '@lendi/analytics-web';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface LaunchDarklyContextType {
  trackingService?: {
    trackEvent: (event: any) => Promise<void>;
    trackPage?: (name: string) => Promise<void>;
    trackCustomEvent?: (event: any) => Promise<void>;
    identityTracking?: (id: string, flags?: any, userTraits?: any) => Promise<void>;
  };
}
interface LaunchDarklyProviderProps {
  children?: ReactNode;
}
export const LaunchDarklyContext = createContext<LaunchDarklyContextType>({
  trackingService: undefined,
});
const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({ children }) => {
  const ldClient = useLDClient();
  const session = useSession();
  const { brand, identity } = session;
  const { analyticsWebV4 } = useFlags();
  const [trackingService, setTrackingService] = useState<LaunchDarklyContextType['trackingService']>(undefined);

  useEffect(() => {
    if (ldClient) {
      const targetId = Cookies.get('targetId');
      const context = {
        anonymous: true,
        key: identity?.id,
        custom: {
          brand,
          ...(targetId && { targetId }),
        },
      };
      ldClient.identify(context);
    }
  }, [ldClient, identity, brand]);

  useEffect(() => {
    if (analyticsWebV4 !== undefined) {
      const tracking = new TrackingService(EventCategory.BOOK_APPOINTMENT, session, analyticsWebV4);
      tracking.identityTracking(identity?.id, {
        type: CustomerType.CUSTOMER,
      });
      setTrackingService(tracking);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsWebV4, identity]);

  return <LaunchDarklyContext.Provider value={{ trackingService }}>{children}</LaunchDarklyContext.Provider>;
};

const LDClientID = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID || '';

const LDProvider = withLDProvider({
  clientSideID: LDClientID,
})(LaunchDarklyProvider) as React.ComponentType<LaunchDarklyProviderProps>;

export default LDProvider;
